import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageShell from '../../components/PageShell.mjs';
import { AuthContext } from '../../authProvider.mjs';
import clarificationService from '../service/clarificationService.mjs';
import { useIsStaff } from '../components/ClarificationBell.mjs';
import { REGIONS } from '../../auditor/page/PTAERegions.mjs';
import {
  STATUS_LABELS, STATUS_PILL, roleLabel, formatWhen, CHANGED_EVENT,
} from '../util/clarificationText.mjs';

/**
 * Every clarification question the reader can see, sorted into what needs
 * them: the questions put to them (or their group), the ones they asked or
 * wrote in, everything still open, and what has finished.
 *
 * The tab lives in the URL (?tab=), so the email links and the bell can land
 * on the right one and Back returns to it.
 *
 * A claimant has only the questions admins have put to them, so they get two
 * tabs: those still open (answered or not) and those finished.
 */
const STAFF_TABS = [
  { key: 'answer', label: 'Needs your answer', params: { scope: 'addressed', status: 'unresolved' } },
  { key: 'mine', label: 'Your questions', params: { scope: 'mine', status: 'unresolved' } },
  { key: 'open', label: 'All open', params: { scope: 'all', status: 'unresolved' } },
  { key: 'ended', label: 'Finished', params: { scope: 'all', status: 'ended' } },
];
const CLAIMANT_TABS = [
  { key: 'mine', label: 'Open questions', params: { scope: 'mine', status: 'unresolved' } },
  { key: 'ended', label: 'Finished', params: { scope: 'all', status: 'ended' } },
];

const ClarificationsInbox = () => {
  const navigate = useNavigate();
  const { isAuditor, isGlobalDirector, isFinanceDirector } = useContext(AuthContext);
  const isGlobal = isAuditor || isGlobalDirector || isFinanceDirector;
  const isStaff = useIsStaff();
  const TABS = isStaff ? STAFF_TABS : CLAIMANT_TABS;
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.find(t => t.key === searchParams.get('tab')) || TABS[0];
  const [region, setRegion] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const params = useMemo(
    () => ({ ...tab.params, ...(isGlobal && region ? { region } : {}) }),
    [tab, isGlobal, region]
  );

  const load = useCallback(async () => {
    try {
      setRows(await clarificationService.list(params));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    window.addEventListener(CHANGED_EVENT, load);
    return () => window.removeEventListener(CHANGED_EVENT, load);
  }, [load]);

  return (
    <PageShell
      title="Questions"
      subtitle={isStaff ? 'Clarification questions on claims and monthly reports' : 'Questions about your claims'}
      width="5xl"
    >
      <div className="bg-white rounded-xl shadow p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <div role="tablist" aria-label="Which questions" className="grid grid-cols-2 sm:flex gap-1 p-1 bg-gray-100 rounded-lg">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab.key === t.key}
                onClick={() => setSearchParams({ tab: t.key })}
                className={`min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  tab.key === t.key ? 'bg-white text-stanford-red shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {isGlobal && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Region
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
              >
                <option value="">All regions</option>
                {REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          )}
        </div>

        {loading && <p className="py-6 text-center text-sm text-gray-500" role="status">Loading questions…</p>}
        {error && <p className="py-3 text-sm text-red-700">{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">
            {tab.key === 'answer' || (!isStaff && tab.key === 'mine')
              ? 'Nothing is waiting on your answer.'
              : 'No questions here.'}
          </p>
        )}

        <ul className="divide-y divide-gray-100">
          {rows.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => navigate(`/clarifications/${t.id}`)}
                className="w-full text-left py-3 px-1 sm:px-2 flex items-start gap-3 hover:bg-gray-50 rounded-lg"
              >
                <span
                  className={`mt-1.5 w-2 h-2 shrink-0 rounded-full ${t.unread ? 'bg-stanford-red' : 'bg-transparent'}`}
                  aria-label={t.unread ? 'New activity' : undefined}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_PILL[t.status] || STATUS_PILL.closed}`}>
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                    {t.needsMyAnswer && (
                      <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-100 text-red-800">For you</span>
                    )}
                    <span className="text-sm font-medium text-gray-900 break-words">
                      {t.subjectType === 'report' ? `Report: ${t.subjectLabel}` : t.subjectLabel}
                    </span>
                  </span>
                  <span className="block mt-0.5 text-sm text-gray-700 break-words">{t.questionPreview}</span>
                  <span className="block mt-0.5 text-xs text-gray-500">
                    {t.askedByName} ({roleLabel(t.askedByRole)}) → {t.directedToLabel}
                    {t.lastMessagePreview && t.lastMessagePreview !== t.questionPreview && (
                      <> · Latest: <span className="italic">{t.lastMessagePreview}</span></>
                    )}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-gray-500 tabular-nums">{formatWhen(t.lastMessageAt)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </PageShell>
  );
};

export default ClarificationsInbox;
